/**
 * Coupons. Mirrors backend/app/api/routes/coupons.py.
 *
 * Both calls send the cart, not a total: a coupon is worth different money on
 * different carts, and the subtotal it is priced against has to be one the
 * server worked out. Nothing here decides a discount — it only renders one.
 *
 * The coupons themselves are tubstudio's; this app neither stores them nor
 * counts how often they have been used.
 */

import { USE_MOCKS } from '@/config';
import { delay, request } from './client';

/**
 * Every coupon the signed-in shopper can actually apply to this cart.
 *
 * Already filtered server-side — expired, exhausted, organisation-restricted and
 * too-generous codes never appear, so anything in this list works when tapped.
 */
export async function listCoupons(items, { signal } = {}) {
  if (USE_MOCKS) {
    await delay(220);
    return mockList(items);
  }
  return request('/api/coupons/available', { method: 'POST', body: { items }, signal });
}

/**
 * Prices one typed code against this cart.
 *
 * Rejections come back as an ApiError whose message is the reason the coupon
 * cannot be used ("that code has expired"), which is what the UI shows.
 */
export async function checkCoupon(items, couponCode, { signal } = {}) {
  if (USE_MOCKS) {
    await delay(220);
    return mockCheck(items, couponCode);
  }
  return request('/api/coupons/check', {
    method: 'POST',
    body: { items, coupon_code: couponCode.trim().toUpperCase() },
    signal,
  });
}

// ---------------------------------------------------------------------------
// Mock coupons — only when there is no backend at all (USE_MOCKS).
//
// Two of them, chosen to exercise both shapes the real service returns and both
// sides of the cart floor: TUB10 always applies, HALFOFF never does on a cart
// this small, so the floor is visible without a backend to configure.
// ---------------------------------------------------------------------------

const MIN_CART_RATIO = 0.75;

const MOCK_COUPONS = [
  { coupon_code: 'TUB10', description: '10% off your order', discount_type: 'percentage', discount_value: 10 },
  { coupon_code: 'TUB50', description: '50% off — big carts only', discount_type: 'percentage', discount_value: 50 },
  { coupon_code: 'FLAT99', description: '₹99 off', discount_type: 'flat', discount_value: 99, currency: 'INR' },
  {
    coupon_code: 'BIG15',
    description: '15% off orders over ₹1000, up to ₹300',
    discount_type: 'percentage',
    discount_value: 15,
    currency: 'INR',
    min_order_amount: 1000,
    max_discount_amount: 300,
  },
];

const round2 = (n) => Math.round(n * 100) / 100;

function mockSubtotal(items) {
  return round2((items ?? []).reduce((sum, i) => sum + (i.lineTotal ?? i.price * i.qty), 0));
}

function mockCurrency(items) {
  return items?.[0]?.currency ?? 'INR';
}

/** The same arithmetic the server uses, so the mock can't flatter a coupon. */
function priceMock(coupon, subtotal, currency) {
  // A coupon may only start applying above an order total, and that minimum is
  // money, so it pins the coupon to its own currency the way a flat one is.
  if (coupon.min_order_amount) {
    if (coupon.currency !== currency) return null;
    if (subtotal < coupon.min_order_amount) return null;
  }
  if (coupon.discount_type === 'flat') {
    if (coupon.currency !== currency) return null;
    return Math.min(coupon.discount_value, subtotal);
  }
  // A percentage carries the most it may ever take off, so a large cart does not
  // get quoted a discount the real service would then cap.
  const discount = round2(subtotal * (coupon.discount_value / 100));
  return coupon.max_discount_amount ? Math.min(discount, coupon.max_discount_amount) : discount;
}

function mockList(items) {
  const subtotal = mockSubtotal(items);
  const currency = mockCurrency(items);
  const maxDiscount = round2(subtotal * (1 - MIN_CART_RATIO));

  const coupons = MOCK_COUPONS.map((c) => {
    const discount = priceMock(c, subtotal, currency);
    if (discount === null || discount <= 0 || discount > maxDiscount) return null;
    return { ...c, discount_amount: discount, final_amount: round2(subtotal - discount), usage_remaining: 1 };
  }).filter(Boolean);

  return { currency, subtotal, max_discount: maxDiscount, min_cart_ratio: MIN_CART_RATIO, coupons };
}

function mockCheck(items, code) {
  const subtotal = mockSubtotal(items);
  const currency = mockCurrency(items);
  const maxDiscount = round2(subtotal * (1 - MIN_CART_RATIO));
  const coupon = MOCK_COUPONS.find((c) => c.coupon_code === code.trim().toUpperCase());

  if (!coupon) throw new Error('That code isn’t working.');

  if (coupon.min_order_amount && subtotal < coupon.min_order_amount) {
    throw new Error(
      `Coupon '${coupon.coupon_code}' applies to orders of ${coupon.min_order_amount} ` +
        `${coupon.currency} or more — add ${round2(coupon.min_order_amount - subtotal)} more to use it.`,
    );
  }

  const discount = priceMock(coupon, subtotal, currency);
  if (discount === null || discount <= 0) {
    throw new Error(`Coupon '${coupon.coupon_code}' can’t be applied to this order.`);
  }
  if (discount > maxDiscount) {
    throw new Error(
      `This code takes more off than this order allows — at most ${maxDiscount} ${currency} can come off this cart.`,
    );
  }
  return { ...coupon, discount_amount: discount, final_amount: round2(subtotal - discount), usage_remaining: 1 };
}
